<script setup lang="ts">
defineOptions({ inheritAttrs: false })

import type { CustomerIntent } from 'shared/src'
import { useDateTime } from '@ai/ui-components'
import { useExport } from '@/composables/exports/useExport'
import { useVariablesAllQuery } from '@/composables/queries/variables'
import VariableDescription from '@/components/BusinessLogic/Variables/VariableDescription.vue'
import { WebComponentTags } from '@/enums/WebComponents'
import { LIST_ACTION_ICONS, ActionButton } from '@/components/BusinessLogic/TableActions'
import { isNoMatchIntent, useDefaultIntent } from '@/composables/configurator/useDefaultIntent'

const { t } = useI18n()
const { formatDateTime } = useDateTime()
const { createNoMatchIntent } = useDefaultIntent()

interface Props {
  label?: string
  subtitle?: string
  required?: boolean
  error?: string
}
const { data: variablesData } = useVariablesAllQuery()
const variableNames = computed(() => new Set((variablesData.value ?? []).map((v) => v.name)))

const { exportToJson } = useExport()
const isExporting = ref(false)
const exportIntents = async () => {
  isExporting.value = true
  await nextTick()

  setTimeout(() => {
    exportToJson(modelValue.value, 'customer_intents')
    isExporting.value = false
  }, 50)
}
const columns = computed(() => [
  { label: t('intent'), key: 'name' },
  { label: t('description'), key: 'description' },
  { label: t('output'), key: 'output' },
  { label: t('created_at'), key: 'created_at' },
])

defineProps<Props>()

const modelValue = defineModel<CustomerIntent[]>({
  required: true,
  default: () => [],
})

const openDeleteModal = ref(false)

const deleteIntent = (item: CustomerIntent) => {
  modelValue.value = modelValue.value.filter((i) => i.name !== item.name)
  openDeleteModal.value = false
  return true
}

const handleIntentSave = (newIntent: CustomerIntent, idx: number | null) => {
  if (idx !== null && idx !== -1) {
    modelValue.value[idx] = newIntent
  } else {
    modelValue.value.push(newIntent)
  }
}

const normalizeImportedIntents = (intents: CustomerIntent[]) => {
  const configured = intents.filter((intent) => !isNoMatchIntent(intent))
  const fallback = intents.find(isNoMatchIntent) ?? createNoMatchIntent()
  modelValue.value = [...configured, fallback]
}

const searchTerm = ref('')
const filteredIntents = computed(() => {
  const term = searchTerm.value.toLowerCase()
  return modelValue.value
    .filter(
      (intent) =>
        intent.name.toLowerCase().includes(term) || intent.output.toLowerCase().includes(term),
    )
    .sort((a, b) => Number(isNoMatchIntent(b)) - Number(isNoMatchIntent(a)))
})
const toggleDeleteModal = (item: CustomerIntent) => {
  selectedForDelete.value = item
  openDeleteModal.value = !openDeleteModal.value
}

const duplicateModal = useTemplateRef('duplicateModalRef')
const selectedForDelete = ref<CustomerIntent>()

const getActions = (item: CustomerIntent, onEdit: (intent: CustomerIntent) => void) => {
  const actions = []

  actions.push({
    label: t('edit'),
    value: 'edit',
    icon: LIST_ACTION_ICONS.edit,
    handler: () => {
      onEdit(item)
    },
    dataTestid: 'action-edit',
  })

  if (isNoMatchIntent(item)) return actions

  actions.push({
    label: t('duplicate'),
    value: 'duplicate',
    icon: LIST_ACTION_ICONS.duplicate,
    handler: () => {
      const copy = { ...item }
      copy.name = ` ${item.name} - ${t('copy')}`
      copy.created_at = new Date().toISOString()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      duplicateModal.value?.openPanel(copy, true)
    },
    dataTestid: 'action-duplicate',
  })

  actions.push({
    label: t('delete'),
    value: 'delete',
    icon: LIST_ACTION_ICONS.deleteDanger,
    handler: () => {
      toggleDeleteModal(item)
    },
    danger: true,
    dataTestid: 'action-delete',
  })

  return actions
}
</script>

<template>
  <OTable
    class="max-h-[calc(100vh-14rem)]"
    id="intents"
    v-model="filteredIntents"
    :columns="columns"
    sortable
    :error
    resizable
    column-resize-mode="indicator"
    dataTestid="intents-list"
  >
    <template #filterActions>
      <div class="flex justify-between items-center w-full">
        <div class="flex space-x-3 items-center">
          <OInput
            v-model="searchTerm"
            iconSearch
            :placeholder="t('search_intents')"
            dataTestid="intents-search"
            size="S"
            class="w-[264px]"
          />
        </div>
        <div class="flex items-center space-x-3 pr-1">
          <IntentsModal ref="duplicateModalRef" @save="(intent) => handleIntentSave(intent, null)">
            <template #default="{ handleClick }">
              <OToolTip
                :shadow-host-selector="WebComponentTags.AiVirtualAgentConfigurator"
                :content="t('create_a_new_intent')"
              >
                <OButton
                  size="M"
                  :label="t('create')"
                  @click="() => handleClick()"
                  dataTestid="intents-btn-add"
                />
              </OToolTip>
            </template>
          </IntentsModal>

          <IntentsImportModal @import="(intents) => normalizeImportedIntents(intents)">
            <template #default="{ handleClick }">
              <OListActions
                small
                placement="bottom-end"
                data-testid="intents-page-actions"
                :actions="[
                  ...(modelValue.length
                    ? [
                        {
                          label: t('export'),
                          value: 'export',
                          handler: exportIntents,
                          disabled: isExporting,
                        },
                      ]
                    : []),
                  {
                    label: t('import'),
                    value: 'import',
                    handler: () => handleClick(),
                  },
                ]"
              />
            </template>
          </IntentsImportModal>
        </div>
      </div>
    </template>

    <template #description="{ item }">
      <VariableDescription
        :text="(item as CustomerIntent).description"
        :variable-names="variableNames"
      />
    </template>

    <template #created_at="{ item }">
      <OOverflowTooltip
        :content="
          formatDateTime({
            format: 'dateTime',
            value: (item as CustomerIntent).created_at,
            fallback: '-',
          })
        "
      >
        {{
          formatDateTime({
            format: 'dateTime',
            value: (item as CustomerIntent).created_at,
            fallback: '-',
          })
        }}
      </OOverflowTooltip>
    </template>

    <template #actions="{ item }">
      <div class="flex items-center space-x-2">
        <IntentsModal
          :is-no-match="isNoMatchIntent(item as CustomerIntent)"
          @save="
            (intent) =>
              handleIntentSave(
                { ...intent, is_no_match: isNoMatchIntent(item as CustomerIntent) },
                modelValue.indexOf(item as CustomerIntent),
              )
          "
        >
          <template #default="{ handleClick }">
            <ActionButton
              shadow-host-selector="ai-virtual-agent-configurator"
              :tooltip="t('edit')"
              icon="edit"
              @click="handleClick(item as CustomerIntent)"
              data-testid="row-action-edit"
            />
            <OListActions
              small
              :actions="getActions(item as CustomerIntent, handleClick)"
              :shadow-host-selector="WebComponentTags.AiVirtualAgentConfigurator"
              data-testid="row-action-more"
            />
          </template>
        </IntentsModal>
      </div>
    </template>
  </OTable>
  <DeleteIntentModal
    v-if="openDeleteModal"
    v-model:open="openDeleteModal"
    :item="(selectedForDelete as CustomerIntent)?.name"
    :secondary-action-promise="() => deleteIntent(selectedForDelete as CustomerIntent)"
  />
</template>
